import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    MenuItem,
    Button,
    useTheme,
    useMediaQuery,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    styled,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormHelperText,
    RadioGroup,
    FormControlLabel,
    Radio,
    Grid,
    Divider,
    IconButton,
    Tooltip,
    InputLabel,
    Select,
    List,
    ListItem,
    ListItemText,
    Menu,
    ListItemIcon,
    Pagination
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import BookMyOpAPI from '../services/BookMyOpapi';
import config from '../config';
// Import icons
import ClearIcon from '@mui/icons-material/Clear';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsIcon from '@mui/icons-material/Directions';

const BASE_URL = config.backend.host;

// Helper function to sort appointments by status: pending → confirmed → completed → cancelled
const sortAppointmentsByStatus = (appointments) => {
    const statusOrder = {
        'pending': 1,
        'confirmed': 2,
        'completed': 3,
        'cancelled': 4
    };
    
    return [...appointments].sort((a, b) => {
        const orderA = statusOrder[a.status] || 99;
        const orderB = statusOrder[b.status] || 99;
        return orderA - orderB;
    });
};

const CANCELLATION_REASONS = [
    // { value: 'patient_requested', label: 'Patient Requested' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'weather_conditions', label: 'Weather Conditions' },
    { value: 'technical_issues', label: 'Technical Issues' },
    { value: 'duplicate_booking', label: 'Duplicate Booking' },
    { value: 'other', label: 'Other' }
];

// Helper function to get status-specific time label
const getStatusTimeLabel = (status) => {
    const labelMap = {
        pending: 'Pending since',
        confirmed: 'Confirmed at',
        cancelled: 'Cancelled at',
        completed: 'Completed at',
        no_show: 'No Show marked at'
    };
    return labelMap[status] || 'Updated at';
};

const StyledCard = styled(Card)(({ theme }) => ({
    backgroundColor: 'white',
    borderRadius: '20px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0, 172, 193, 0.1)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    transition: theme.transitions.create(['transform', 'box-shadow'], {
        duration: theme.transitions.duration.standard,
    }),
    '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: '0 12px 48px rgba(0, 172, 193, 0.15)',
    }
}));

const StyledChip = styled(Chip)(({ theme, status }) => ({
    borderRadius: '12px',
    height: 26,
    padding: '0 6px',
    backgroundColor: status === 'pending' ? '#ff9800' : status === 'confirmed' ? '#4caf50' : status === 'completed' ? '#2196f3' : '#f44336',
    color: theme.palette.common.white,
    fontWeight: 700,
    '& .MuiChip-label': {
        padding: '0 6px',
        fontSize: '0.8rem',
        fontWeight: 800,
        textTransform: 'capitalize',
        letterSpacing: '0.5px'
    }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: theme.typography.fontFamily,
    '&.MuiTableCell-head': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 800,
        fontSize: '0.9rem',
        letterSpacing: '0.5px'
    },
    '&.MuiTableCell-body': {
        fontWeight: 800
    }
}));

const CancelDialog = ({ open, onClose, onConfirm, appointment }) => {
    const [reason, setReason] = useState('');
    const [error, setError] = useState(false);

    const handleConfirm = () => {
        if (!reason) {
            setError(true);
            return;
        }
        onConfirm(reason);
        setReason('');
        setError(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Cancel Appointment</DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Please select a reason for cancelling your appointment with Dr. {appointment?.doctor_name}
                </Typography>
                <FormControl error={error} fullWidth>
                    <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
                        {CANCELLATION_REASONS.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio />}
                                label={option.label}
                            />
                        ))}
                    </RadioGroup>
                    {error && <FormHelperText>Please select a reason for cancellation</FormHelperText>}
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Back</Button>
                <Button onClick={handleConfirm} variant="contained" color="error">
                    Confirm Cancellation
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DocumentViewerDialog = ({ open, onClose, document }) => {
    const theme = useTheme();
    
    if (!document) return null;

    const isImage = document.document_url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    const isPDF = document.document_url.match(/\.pdf$/i);
    const fullUrl = document.document_url.startsWith('/') ? `${BASE_URL}${document.document_url}` : document.document_url;

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { height: '90vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ListItemIcon>
                        {isPDF ? (
                            <PictureAsPdfIcon sx={{ color: theme.palette.error.main }} />
                        ) : isImage ? (
                            <ImageIcon sx={{ color: theme.palette.primary.main }} />
                        ) : (
                            <DescriptionIcon sx={{ color: theme.palette.secondary.main }} />
                        )}
                    </ListItemIcon>
                    <Box>
                        <Typography variant="h6">{document.document_url.split('/').pop()}</Typography>
                        <Typography variant="caption" color="text.primary">
                            {document.document_type} • Uploaded on {format(new Date(document.uploaded_at), 'MMM d, yyyy h:mm a')}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {isImage ? (
                    <Box
                        component="img"
                        src={fullUrl}
                        alt={document.document_url.split('/').pop()}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            backgroundColor: '#f5f5f5'
                        }}
                    />
                ) : isPDF ? (
                    <iframe
                        src={fullUrl}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title={document.document_url.split('/').pop()}
                    />
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        <DescriptionIcon sx={{ fontSize: 64, color: theme.palette.text.primary }} />
                        <Typography variant="h6" color="text.primary">
                            Document Preview Not Available
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => window.open(fullUrl, '_blank')}
                        >
                            Open Document
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Close</Button>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                        const link = document.createElement('a');
                        link.href = fullUrl;
                        link.download = document.document_url.split('/').pop();
                        link.click();
                    }}
                >
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const AppointmentDetailsDialog = ({ open, onClose, appointment, onCancelClick }) => {
    const theme = useTheme();
    const [uploadingDocuments, setUploadingDocuments] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [documentType, setDocumentType] = useState('');
    const [fileError, setFileError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [viewerDialog, setViewerDialog] = useState({
        open: false,
        document: null
    });
    const [documentMenu, setDocumentMenu] = useState({
        anchorEl: null,
        document: null
    });
    const [removingDocument, setRemovingDocument] = useState(false);

    // Document type options
    const documentTypeOptions = [
        { value: 'Prescription', label: 'Prescription' },
        { value: 'Medical Report', label: 'Medical Report' },
        { value: 'Medical Scan', label: 'Medical Scan' },
        { value: 'Test Result', label: 'Test Result' },
        { value: 'Other', label: 'Other' }
    ];

    useEffect(() => {
        if (appointment?.patient_documents) {
            setDocuments(appointment.patient_documents);
        }
    }, [appointment]);

    // File validation helper
    const validateFile = (file) => {
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const maxSize = 10 * 1024 * 1024; // 10MB
        
        if (!validTypes.includes(file.type)) {
            throw new Error('Invalid file type. Only PDF and images are allowed.');
        }
        
        if (file.size > maxSize) {
            throw new Error('File size exceeds 10MB limit.');
        }
        
        return true;
    };

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        setFileError('');
        setUploadSuccess(false);
        
        try {
            // Validate files
            files.forEach(file => {
                if (!validateFile(file)) {
                    throw new Error(`Invalid file: ${file.name}`);
                }
            });

            setUploadingDocuments(true);

            // Create FormData
            const formData = new FormData();
            formData.append('appointment_id', appointment.id);
            
            // Add each file with its document type
            files.forEach((file, index) => {
                formData.append('documents', file);
                formData.append('document_type', documentType);
            });

            console.log('Uploading documents for appointment:', appointment.id, {
                appointmentId: appointment.id,
                documentType,
                filesCount: files.length
            });
            
            // Call API to upload documents
            const response = await BookMyOpAPI.uploadAppointmentDocuments(formData);
            console.log('Upload response:', response);
            
            if (response.data?.documents) {
                // Update documents list with new documents
                setDocuments(prev => [...prev, ...response.data.documents]);
                setUploadSuccess(true);
                setDocumentType('');
            } else {
                throw new Error('Invalid response format from server');
            }

        } catch (error) {
            console.error('Error uploading documents:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to upload documents. Please try again.';
            setFileError(errorMessage);
        } finally {
            setUploadingDocuments(false);
            // Clear the file input
            event.target.value = '';
        }
    };

    const handleViewDocument = (document) => {
        setViewerDialog({
            open: true,
            document
        });
    };

    const handleDownloadDocument = (documentUrl) => {
        // If the URL is relative (starts with /), prepend the BASE_URL
        const fullUrl = documentUrl.startsWith('/') ? `${BASE_URL}${documentUrl}` : documentUrl;
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = documentUrl.split('/').pop();
        link.click();
    };

    const handleRemoveDocument = async (document) => {
        try {
            setRemovingDocument(true);
            
            // Call API to remove document
            await BookMyOpAPI.removeAppointmentDocument(document.id);
            
            // Remove from local state
            setDocuments(prev => prev.filter(doc => doc.id !== document.id));
            
            // Close menu
            setDocumentMenu({
                anchorEl: null,
                document: null
            });
            
        } catch (error) {
            console.error('Error removing document:', error);
            setFileError('Failed to remove document. Please try again.');
        } finally {
            setRemovingDocument(false);
        }
    };

    const handleDocumentMenuOpen = (event, document) => {
        setDocumentMenu({
            anchorEl: event.currentTarget,
            document
        });
    };

    const handleDocumentMenuClose = () => {
        setDocumentMenu({
            anchorEl: null,
            document: null
        });
    };
    
    if (!appointment) return null;

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="sm"
                fullWidth
            >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5">Appointment</Typography>
                    <StyledChip 
                        label={appointment.status}
                        status={appointment.status}
                    />
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* Doctor Information */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            {appointment.doctor_profile_photo ? (
                                <Box
                                    component="img"
                                    src={appointment.doctor_profile_photo}
                                    alt={appointment.doctor_name}
                                    sx={{ 
                                        width: 50, 
                                        height: 50, 
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{ 
                                        width: 50, 
                                        height: 50,     
                                        borderRadius: '50%',
                                        backgroundColor: theme.palette.primary.light,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <LocalHospitalIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                                </Box>
                            )}
                            <Box sx={{ flex: 1 }}>
                                <Typography  sx={{ fontWeight: 700, mb: 1 ,fontSize: '1rem'}}>
                                    Dr. {appointment.doctor_name}
                                </Typography>
                                <Typography  sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 1,fontSize: '0.8rem'}}>
                                    {appointment.specialty_name}
                                </Typography>
                              
                            </Box>
                            
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.primary', mb: 0.5, lineHeight: 1.4 }}>
                                    {appointment.doctor_qualifications}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                                    {appointment.doctor_experience} years experience
                                </Typography>
                        <Divider />
                    </Grid>

                    {/* Hospital Information */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Hospital Details</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                            <LocationOnIcon sx={{ color: theme.palette.primary.main }} />
                            <Box>
                                <Typography variant="body1">{appointment.service_provider_name}</Typography>
                                <Typography variant="body1" color="text.primary">
                                    {appointment.service_provider_address}
                                </Typography>
                                {appointment.service_provider_coordinator_1_number && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        <PhoneIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => window.open(`tel:${appointment.service_provider_coordinator_1_number}`, '_self')}
                                            sx={{ p: 0, minWidth: 'auto', textTransform: 'none', color: theme.palette.primary.main }}
                                        >
                                            {appointment.service_provider_coordinator_1_number}
                                        </Button>
                                    </Box>
                                )}
                                {appointment.service_provider_location && (
                                    <Button
                                        variant="text"
                                        size="small"
                                        href={appointment.service_provider_location}
                                        target="_blank"
                                        startIcon={<OpenInNewIcon />}
                                        sx={{ mt: 0.5, p: 0 }}
                                    >
                                        View on Maps
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <Divider />
                    </Grid>

                    {/* Patient Information */}
                    {/* <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Patient Details</Typography>
                        <Typography variant="body1">{appointment.patient_name}</Typography>
                        <Typography variant="body1" color="text.primary">
                            Contact: {appointment.patient_contact}
                        </Typography>
                        <Divider sx={{ mt: 2 }} />
                    </Grid> */}

                    {/* Appointment Details */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Appointment Details</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarTodayIcon sx={{ color: theme.palette.primary.main }} />
                                    <Typography variant="body1">
                                        {format(new Date(appointment.appointment_date), 'MMMM d, yyyy')}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
                                    <Typography variant="body1">
                                        {format(new Date(`2000-01-01T${appointment.appointment_time}`), 'h:mm a')}
                                    </Typography>
                                </Box>
                            </Grid>
                            {appointment.updated_at && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {getStatusTimeLabel(appointment.status)}:{' '}
                                        </Typography>
                                        <Typography variant="body1">
                                            {format(new Date(appointment.updated_at), 'MMM d, yyyy h:mm a')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Typography variant="body1" sx={{ mt: 1 }}>
                                    Consultation Fee: <span style={{ color: theme.palette.primary.main, fontWeight: 500 }}>₹{appointment.consultation_fee}</span>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Documents Section */}
                  
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Close</Button>
                {['pending', 'confirmed'].includes(appointment.status) && (
                    <Button 
                        variant="contained" 
                        color="error" 
                        startIcon={<CancelIcon />}
                        onClick={() => {
                            onClose();
                            onCancelClick(appointment);
                        }}
                    >
                        Cancel Appointment
                    </Button>
                )}
            </DialogActions>
        </Dialog>

        {/* Document Viewer Dialog */}
        <DocumentViewerDialog
            open={viewerDialog.open}
            onClose={() => setViewerDialog({ open: false, document: null })}
            document={viewerDialog.document}
        />
    </>
    );
};

const MobileAppointmentCard = ({ appointment, onCancelClick, onViewDetails }) => {
    const theme = useTheme();

    return (
        <StyledCard>
            <CardContent sx={{ p: 2 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    mb: 2,
                    width: '100%'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {appointment.doctor_profile_photo ? (
                            <Box
                                component="img"
                                src={appointment.doctor_profile_photo}
                                alt={appointment.doctor_name}
                                sx={{ 
                                    width: 50, 
                                    height: 50, 
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    flexShrink: 0
                                }}
                            />
                        ) : (
                            <LocalHospitalIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900, fontSize: '1rem' }}>
                                Dr. {appointment.doctor_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem', color: theme.palette.primary.main }}>
                                    {appointment.specialty_name}
                                </Typography>
                                <StyledChip 
                                    label={appointment.status}
                                    status={appointment.status}
                                />
                            </Box>
                        </Box>
                    </Box>
                 
                </Box>

                <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                            {format(new Date(appointment.appointment_date), 'MMM d, yyyy')} at {' '}
                            {format(new Date(`2000-01-01T${appointment.appointment_time}`), 'h:mm a')}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary' }}>
                            {appointment.service_provider_name}
                        </Typography>
                    </Box>

                    {/* Contact and Directions Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        {appointment.service_provider_coordinator_1_number && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PhoneIcon fontSize="small" />}
                                onClick={() => window.open(`tel:${appointment.service_provider_coordinator_1_number}`, '_self')}
                                sx={{
                                    color: theme.palette.success.main,
                                    borderColor: theme.palette.success.main,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    '&:hover': {
                                        borderColor: theme.palette.success.main,
                                        backgroundColor: 'rgba(76, 175, 80, 0.04)'
                                    }
                                }}
                            >
                                Call
                            </Button>
                        )}
                        
                        {appointment.service_provider_location && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DirectionsIcon fontSize="small" />}
                                onClick={() => window.open(appointment.service_provider_location, '_blank')}
                                sx={{
                                    color: theme.palette.info.main,
                                    borderColor: theme.palette.info.main,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    '&:hover': {
                                        borderColor: theme.palette.info.main,
                                        backgroundColor: 'rgba(33, 150, 243, 0.04)'
                                    }
                                }}
                            >
                                Directions
                            </Button>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                     
                        <Stack direction="row" spacing={1}>
                   
                            <Button 
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon fontSize="small" />}
                                onClick={() => onViewDetails(appointment)}
                                sx={{ 
                                    color: theme.palette.primary.main,
                                    borderColor: theme.palette.primary.main,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: 'rgba(0, 172, 193, 0.04)'
                                    }
                                }}
                            >
                                View Details
                            </Button>
                            {['pending', 'confirmed'].includes(appointment.status) && (
                                <Button 
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CancelIcon fontSize="small" />}
                                    onClick={() => onCancelClick(appointment)}
                                    sx={{ 
                                        color: theme.palette.error.main,
                                        borderColor: theme.palette.error.main,
                                        textTransform: 'none',
                                        '&:hover': {
                                            borderColor: theme.palette.error.main,
                                            backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </CardContent>
        </StyledCard>
    );
};

const AppointmentsTable = ({ appointments, onCancelClick, onViewDetails }) => {
    const theme = useTheme();

    return (
        <TableContainer component={Paper} sx={{ 
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0, 172, 193, 0.1)',
            borderRadius: '20px',
            overflow: 'hidden',
            backgroundColor: 'white'
        }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <StyledTableCell>Doctor</StyledTableCell>
                        <StyledTableCell>Hospital</StyledTableCell>
                        <StyledTableCell>Date & Time</StyledTableCell>
                        <StyledTableCell>Fee</StyledTableCell>
                        <StyledTableCell>Status</StyledTableCell>
                        <StyledTableCell align="right">Actions</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {appointments.map((appointment) => (
                        <TableRow 
                            key={appointment.id}
                            sx={{ 
                                '&:hover': { 
                                    backgroundColor: `${theme.palette.primary.main}05` 
                                }
                            }}
                        >
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {appointment.doctor_profile_photo ? (
                                        <Box
                                            component="img"
                                            src={appointment.doctor_profile_photo}
                                            alt={appointment.doctor_name}
                                            sx={{ 
                                                width: 40, 
                                                height: 40, 
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        <LocalHospitalIcon sx={{ color: theme.palette.primary.main }} />
                                    )}
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                                            Dr. {appointment.doctor_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                            {appointment.specialty_name}
                                        </Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ fontWeight: 700 }}>{appointment.service_provider_name}</Typography>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                        {format(new Date(appointment.appointment_date), 'MMM d, yyyy')}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                        {format(new Date(`2000-01-01T${appointment.appointment_time}`), 'h:mm a')}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body1" sx={{ color: theme.palette.primary.main, fontWeight: 800, fontSize: '1rem' }}>
                                    ₹{appointment.consultation_fee}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <StyledChip 
                                    label={appointment.status}
                                    status={appointment.status}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    {appointment.service_provider_coordinator_1_number && (
                                        <Button 
                                            size="small"
                                            variant="outlined"
                                            startIcon={<PhoneIcon fontSize="small" />}
                                            onClick={() => window.open(`tel:${appointment.service_provider_coordinator_1_number}`, '_self')}
                                            sx={{ 
                                                color: theme.palette.success.main,
                                                borderColor: theme.palette.success.main,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: theme.palette.success.main,
                                                    backgroundColor: 'rgba(76, 175, 80, 0.04)'
                                                }
                                            }}
                                        >
                                            Call
                                        </Button>
                                    )}
                                    {appointment.service_provider_location && (
                                        <Button 
                                            size="small"
                                            variant="outlined"
                                            startIcon={<DirectionsIcon fontSize="small" />}
                                            onClick={() => window.open(appointment.service_provider_location, '_blank')}
                                            sx={{ 
                                                color: theme.palette.info.main,
                                                borderColor: theme.palette.info.main,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: theme.palette.info.main,
                                                    backgroundColor: 'rgba(33, 150, 243, 0.04)'
                                                }
                                            }}
                                        >
                                            Directions
                                        </Button>
                                    )}
                                    <Button 
                                        size="small"
                                        variant="outlined"
                                        startIcon={<VisibilityIcon fontSize="small" />}
                                        onClick={() => onViewDetails(appointment)}
                                        sx={{ 
                                            color: theme.palette.primary.main,
                                            borderColor: theme.palette.primary.main,
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: theme.palette.primary.main,
                                                backgroundColor: 'rgba(0, 172, 193, 0.04)'
                                            }
                                        }}
                                    >
                                        View Details
                                    </Button>
                                    {['pending', 'confirmed'].includes(appointment.status) && (
                                        <Button 
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CancelIcon fontSize="small" />}
                                            onClick={() => onCancelClick(appointment)}
                                            sx={{ 
                                                color: theme.palette.error.main,
                                                borderColor: theme.palette.error.main,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: theme.palette.error.main,
                                                    backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                                }
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                </Stack>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const BookmyopAppointments = () => {
    const { user } = useBookMyOpAuth();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        date: null
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: 10
    });
    const [cancelDialog, setCancelDialog] = useState({
        open: false,
        appointment: null
    });
    const [detailsDialog, setDetailsDialog] = useState({
        open: false,
        appointment: null
    });
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleCancelClick = (appointment) => {
        setCancelDialog({
            open: true,
            appointment
        });
    };

    const handleCancelConfirm = async (reason) => {
        try {
            await BookMyOpAPI.cancelAppointment(cancelDialog.appointment.id, reason);
            
            // Refresh appointments on current page
            await fetchAppointments(pagination.currentPage);
            
            // Close dialog
            setCancelDialog({
                open: false,
                appointment: null
            });
        } catch (err) {
            setError('Failed to cancel appointment. Please try again.');
        }
    };

    const handleViewDetails = (appointment) => {
        setDetailsDialog({
            open: true,
            appointment
        });
    };

    const fetchAppointments = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            setError(null);
            
            const queryParams = {
                page: page,
                page_size: pagination.pageSize
            };
            if (filters.status) {
                queryParams.status = filters.status;
            }
            if (filters.date) {
                queryParams.appointment_date = format(filters.date, 'yyyy-MM-dd');
            }
            
            const response = await BookMyOpAPI.getAppointments(queryParams);
            
            // Determine actual page size from API response
            // Priority: 1) Extract from next/previous URLs, 2) Use number of results if it's a full page, 3) Fallback to requested page size
            let actualPageSize = pagination.pageSize;
            
            // Try to extract from next URL first (most reliable)
            if (response.next) {
                try {
                    const nextUrl = new URL(response.next);
                    const pageSizeParam = nextUrl.searchParams.get('page_size');
                    if (pageSizeParam) {
                        actualPageSize = parseInt(pageSizeParam, 10);
                    }
                } catch (e) {
                    // URL parsing failed, continue with other methods
                }
            }
            
            // If no next URL, try previous URL
            if (actualPageSize === pagination.pageSize && response.previous) {
                try {
                    const prevUrl = new URL(response.previous);
                    const pageSizeParam = prevUrl.searchParams.get('page_size');
                    if (pageSizeParam) {
                        actualPageSize = parseInt(pageSizeParam, 10);
                    }
                } catch (e) {
                    // URL parsing failed, continue with other methods
                }
            }
            
            // If still using default and we have results, check the actual number returned
            // If API returned more items than requested, it's using a different page size
            if (actualPageSize === pagination.pageSize && response.results && response.results.length > 0) {
                const resultsCount = response.results.length;
                const isLastPage = !response.next;
                
                // If API returned more items than we requested, use that as the actual page size
                if (resultsCount > pagination.pageSize) {
                    actualPageSize = resultsCount;
                } 
                // If this is a full page (not last page) and matches requested size, use requested size
                else if (!isLastPage && resultsCount === pagination.pageSize) {
                    actualPageSize = pagination.pageSize;
                }
                // If last page with fewer results, keep using requested page size
                // (the page size is what was requested, not the number of results)
            }
            
            // Update pagination state - use count from API response
            const totalCount = response.count || 0;
            const calculatedTotalPages = totalCount > 0 ? Math.ceil(totalCount / actualPageSize) : 1;
            // Ensure current page doesn't exceed total pages - if invalid, use page 1
            const validPage = (page > calculatedTotalPages && calculatedTotalPages > 0) ? 1 : page;
            
            // Only process results if page is valid
            if (validPage === page) {
                let appointmentsList = response.results || [];
                
                // Sort by status only when showing all appointments (no status filter)
                if (!filters.status) {
                    appointmentsList = sortAppointmentsByStatus(appointmentsList);
                }
                
                setAppointments(appointmentsList);
            } else {
                // Page was invalid, clear appointments
                setAppointments([]);
            }
            
            setPagination(prev => ({
                ...prev,
                currentPage: validPage,
                totalPages: calculatedTotalPages,
                totalCount: totalCount,
                pageSize: actualPageSize // Update pageSize to match API's actual page size
            }));
        } catch (err) {
            setError(err.message || 'Failed to fetch appointments');
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.pageSize]);

    const handlePageChange = (event, page) => {
        fetchAppointments(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        if (user) {
            fetchAppointments(1); // Reset to page 1 when filters change
        }
    }, [user, fetchAppointments]);

    if (!user) {
        return (
            <Container maxWidth="lg">
                <Alert severity="warning" sx={{ mt: 4 }}>
                    Please log in to view your appointments
                </Alert>
            </Container>
        );
    }

    return (
        <Container 
            maxWidth="lg" 
            sx={{ 
              
                backgroundColor: theme.palette.background.default,
                minHeight: '100vh'
            }}
        >
            <Box sx={{ mb: 4 }}>
                <Typography 
                    variant="h4" 
                    sx={{ 
                        mb: 1,
                        color: theme.palette.text.primary,
                        fontWeight: 800,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.02em'
                    }}
                >
                    My Appointments
                </Typography>
                <Typography 
                    variant="body1" 
                    sx={{ 
                        color: theme.palette.text.primary,
                        fontWeight: 800,
                        fontSize: { xs: '0.95rem', md: '1rem' },
                        opacity: 0.8
                    }}
                >
                    Manage your upcoming and past medical appointments
                </Typography>
            </Box>

            <Paper 
                elevation={0}
                sx={{ 
                    p: 3,
                    mb: 3,
                    width:'100%',
                    backgroundColor: 'white',
                    border: '1px solid rgba(0, 172, 193, 0.1)',
                    borderRadius: '20px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}
            >
                <Grid container spacing={1} alignItems="center" sx={{display:'flex',justifyContent:'space-between',flexDirection:'row'}}>
                    <Grid item xs={6} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel 
                                shrink={true}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                }}
                            >
                                Status
                            </InputLabel>
                            <Select
                                value={filters.status || ''}
                                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected || selected === '') {
                                        return 'All Status';
                                    }
                                    return selected.charAt(0).toUpperCase() + selected.slice(1);
                                }}
                                sx={{
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    height: '40px',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    '& .MuiSelect-select': {
                                        padding: '8px 14px',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="confirmed">Confirmed</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* <Grid item xs={6} md={4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                value={filters.date}
                                size="small"
                                onChange={(newDate) => setFilters(prev => ({ ...prev, date: newDate }))}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        fullWidth
                                        size="small"
                                        placeholder="Filter by date"
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid> */}

                    <Grid item xs={6} md={4}>
                        <Button
                            onClick={() => setFilters({ status: '', date: null })}
                            variant="outlined"
                            size="small"
                            fullWidth={isMobile}
                            sx={{ 
                                color: theme.palette.primary.main,
                                borderColor: theme.palette.primary.main,
                                fontWeight: 700,
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                height: '40px',
                                px: 2,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.light + '20',
                                    borderColor: theme.palette.primary.main,
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }
                            }}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    py: 8,
                    backgroundColor: 'white',
                    borderRadius: '20px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(0, 172, 193, 0.1)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}>
                    <CircularProgress size={50} sx={{ color: theme.palette.primary.main }} />
                </Box>
            )}

            {error && (
                <Alert 
                    severity="error" 
                    sx={{ 
                        mb: 3,
                        borderRadius: '12px',
                        '& .MuiAlert-message': {
                            fontWeight: 800
                        }
                    }}
                    action={
                        <Button 
                            color="inherit" 
                            size="small" 
                            onClick={fetchAppointments}
                            sx={{ fontWeight: 700 }}
                        >
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {!loading && !error && appointments.length === 0 && (
                <Box 
                    sx={{ 
                        textAlign: 'center',
                        py: 8,
                        px: 2,
                        backgroundColor: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid rgba(0, 172, 193, 0.1)',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                    }}
                >
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                        No Appointments Found
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary' }}>
                        There are no appointments matching your filters
                    </Typography>
                </Box>
            )}

            {!loading && !error && appointments.length > 0 && (
                <>
                    {isMobile ? (
                        <Stack spacing={2}>
                            {appointments.map((appointment) => (
                                <MobileAppointmentCard 
                                    key={appointment.id} 
                                    appointment={appointment}
                                    onCancelClick={handleCancelClick}
                                    onViewDetails={handleViewDetails}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <AppointmentsTable 
                            appointments={appointments}
                            onCancelClick={handleCancelClick}
                            onViewDetails={handleViewDetails}
                        />
                    )}
                    
                    {/* Pagination */}
                    {pagination.totalCount > 0 && pagination.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                            <Pagination
                                count={pagination.totalPages}
                                page={pagination.currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        fontWeight: 700,
                                        fontSize: isMobile ? '0.85rem' : '1rem'
                                    }
                                }}
                            />
                        </Box>
                    )}
                </>
            )}

            <CancelDialog
                open={cancelDialog.open}
                onClose={() => setCancelDialog({ open: false, appointment: null })}
                onConfirm={handleCancelConfirm}
                appointment={cancelDialog.appointment}
            />

            <AppointmentDetailsDialog
                open={detailsDialog.open}
                onClose={() => setDetailsDialog({ open: false, appointment: null })}
                appointment={detailsDialog.appointment}
                onCancelClick={handleCancelClick}
            />
        </Container>
    );
};

export default BookmyopAppointments;
